/** @jsxImportSource @emotion/react */
import type { OperationsTeamNode } from '@sportninja/common/features/competition/operations/types/operations.types'
import type { StandingsRow } from '@sportninja/common/features/competition/operations/types/standings.types'
import { applyLocalOrder } from '@sportninja/common/features/competition/operations/utils/applyLocalOrder'
import { resolveRowDragAfter } from '@sportninja/common/features/competition/operations/utils/resolveRowDragAfter'
import { indexStandingsByTeam } from '@sportninja/common/features/competition/operations/utils/standingsByTeam'
import css from '@emotion/css/macro'
import { useState } from 'react'
import type { GridApi } from 'ag-grid-community'
import DataGrid from 'src/components/ts-components/DataGrid'
import { RECORD_CELL_RESERVED_WIDTH } from '../cells/RecordCell'
import RecordLegend from './RecordLegend'
import {
  OPERATIONS_GRID_HEADER_HEIGHT,
  OPERATIONS_GRID_ROW_HEIGHT,
  operationsGridStyle,
} from '../operationsGridStyle'
import { shouldIgnoreCellClick } from '../../utils/isActionsCellClick'
import { rowDragIcon } from '../dnd/dragHandleIcon'
import {
  useDivisionTeamsColumns,
  type DivisionTeamRow,
  type RowAction,
} from '../../grid/useDivisionTeamsColumns'

interface DivisionTeamsGridProps {
  /**
   * The node these rows belong to. `useOperationsDnd` keys its dropped order by
   * the PARENT id, so the local order cannot be read without it.
   */
  divisionId: string
  teams: OperationsTeamNode[]
  canEdit: boolean
  /** Drops Delete from the row menu while registration is open. */
  isRegistrationOn?: boolean
  onSelectTeam: (nodeId: string) => void
  onRowAction: (action: RowAction, row: DivisionTeamRow) => void
  onDrop: (activeId: string, overId: string | null, after: boolean) => void
  localOrder: Record<string, string[]>
  dragPendingIds: Set<string>
  /**
   * In-division records and points, joined onto each row by `teamId`. Empty
   * while loading and for a division that publishes no standings.
   */
  standingsRows?: StandingsRow[]
  /** Adds the record/PTS columns. False for a division without standings. */
  showStandings?: boolean
  isStandingsLoading?: boolean
  /** Renames the record column's header — conferences are not divisions. */
  isConference?: boolean
}

/**
 * The division's teams as a read-only sheet (seed is read-only — see
 * useDivisionTeamsColumns). Row click opens that team's detail panel.
 *
 * Reordering uses AG GRID's native row drag rather than dnd-kit. The grid owns
 * its row elements, so a dnd-kit handle could only ever move the 36px grip —
 * the row itself never followed the cursor and no drop indicator was drawn. The
 * tree rail still uses dnd-kit, because it renders its own rows.
 */
const DivisionTeamsGrid = ({
  divisionId,
  teams,
  canEdit,
  isRegistrationOn = false,
  onSelectTeam,
  onRowAction,
  onDrop,
  localOrder,
  dragPendingIds,
  standingsRows = [],
  showStandings = false,
  isStandingsLoading = false,
  isConference = false,
}: DivisionTeamsGridProps) => {
  'use memo'

  const standings = indexStandingsByTeam(standingsRows)

  // Scaled against the division's busiest team, exactly as the Division
  // Performance chart does — so a bar means the same thing in both places.
  const maxGamesPlayed = Math.max(
    0,
    ...standingsRows.map((row) => row.record?.gamesPlayed ?? 0)
  )

  // ONE width for the whole column, read from AG Grid itself rather than
  // measured per cell. A division can hold 20-50 teams, and a ResizeObserver
  // inside each `RecordCell` would mean that many observers all firing — and
  // all re-rendering — on a single panel resize. AG Grid already knows its
  // column widths and tells us when they change.
  const [recordColumnWidth, setRecordColumnWidth] = useState(0)

  // The bar's track is the cell minus its padding, the record text and the gap
  // between them. Kept beside `RecordCell`'s own styles, which define those.
  const recordTrackWidth = Math.max(
    0,
    recordColumnWidth - RECORD_CELL_RESERVED_WIDTH
  )

  const readRecordWidth = (api: GridApi<DivisionTeamRow>) => {
    const column = api.getColumn('record')
    if (column) {
      setRecordColumnWidth(Math.round(column.getActualWidth()))
    }
  }

  const columns = useDivisionTeamsColumns({
    canEdit,
    isRegistrationOn,
    onRowAction,
    showStandings,
    maxGamesPlayed,
    isStandingsLoading,
    recordTrackWidth,
    isConference,
  })

  // Seed edits are read-only (see useDivisionTeamsColumns), so a drag save is
  // the only thing that can mark a row pending.
  const rows: DivisionTeamRow[] = applyLocalOrder(
    teams.map((team) => ({
      ...team,
      pending: dragPendingIds.has(team.teamId),
      // Spread, not `?? undefined`: a team missing from standings must stay
      // undefined, which the cells render as blank rather than as a zero.
      ...standings[team.teamId],
    })),
    localOrder[divisionId]
  )

  return (
    <div css={wrapStyle}>
      <div css={operationsGridStyle}>
        <DataGrid<DivisionTeamRow>
          rowData={rows}
          columnDefs={columns}
          getRowId={({ data }) => data.id}
          domLayout='autoHeight'
          headerHeight={OPERATIONS_GRID_HEADER_HEIGHT}
          rowHeight={OPERATIONS_GRID_ROW_HEIGHT}
          rowClassRules={{
            'operations-row-pending': ({ data }) => Boolean(data?.pending),
          }}
          // Grab anywhere on the row, not just a grip — the whole row lifts and
          // follows the cursor, which is what a drag should look like.
          // Not while a move is saving, and not without permission: a second drag
          // over an in-flight row would race the first.
          rowDragEntireRow={canEdit && dragPendingIds.size === 0}
          // `rowDragManaged` is what draws the drop indicator; without it AG Grid
          // does nothing visual at all. Pairing it with
          // `suppressMoveWhenRowDragging` gets the indicator WITHOUT the grid
          // reordering its own client-side model — it highlights the target and
          // commits nothing until mouse-up. That keeps this feature's rule that
          // the server is the single source of truth: the drag is presentation,
          // and the order still comes from the refetched tree.
          rowDragManaged
          suppressMoveWhenRowDragging
          // Otherwise the drag image is AG Grid's default "1 row" chip.
          rowDragText={({ rowNode }) =>
            (rowNode?.data as DivisionTeamRow | undefined)?.name ?? ''
          }
          // The same six-dot grip the tree rail shows, replacing AG Grid's own
          // drag glyph so one gesture does not look like two features.
          icons={{ rowDrag: rowDragIcon }}
          onRowDragEnd={(event) => {
            const overNode = event.overNode
            const activeId = event.node.data?.id
            const overId = overNode?.data?.id
            if (!activeId || !overId || activeId === overId) {
              return
            }
            // Whether the drop lands below the target comes from geometry, not
            // from `vDirection`: that field reports only the last mouse tick and
            // is null when the pointer settles, which read as "above" on nearly
            // every drop and swallowed the save. `y`, `rowTop` and `rowHeight`
            // share the "0 = top of first row" space (see resolveRowDragAfter).
            const after =
              overNode.rowTop != null && overNode.rowHeight != null
                ? resolveRowDragAfter({
                    pointerY: event.y,
                    rowTop: overNode.rowTop,
                    rowHeight: overNode.rowHeight,
                  })
                : event.vDirection === 'down'
            onDrop(activeId, overId, after)
          }}
          // Selection is driven from CELL clicks, not row clicks: only the cell
          // event carries `colDef`, and an editable cell must be left to enter
          // edit mode. Selecting re-renders the grid, which would tear the cell
          // back out before it could take focus. (The roster grids sidestep this
          // by having no row click at all — see RosterGrid.)
          onCellClicked={(event) => {
            if (
              event.data &&
              !shouldIgnoreCellClick(event.colDef, event.data, event.event)
            ) {
              onSelectTeam(event.data.id)
            }
          }}
          // Column width comes from AG Grid rather than a per-cell observer;
          // these are the three moments it can change.
          onGridReady={({ api }) => readRecordWidth(api)}
          onGridSizeChanged={({ api }) => readRecordWidth(api)}
          onColumnResized={({ api }) => readRecordWidth(api)}
          data-testid='division-teams-grid'
        />
      </div>

      {/* Only alongside the record column it explains — a division without
          standings has no bars to key. */}
      {showStandings && (
        <div css={legendRowStyle}>
          <RecordLegend />
        </div>
      )}
    </div>
  )
}

const wrapStyle = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`

/* Left-aligned under the table. `min-width: 0` lets the legend's own
   horizontal scroll kick in on a narrow panel instead of stretching this row. */
const legendRowStyle = css`
  display: flex;
  min-width: 0;
`

export default DivisionTeamsGrid
